import type { TileData, TileType } from '../types';
import { GRID_SIZE } from '../config';
import { createSeededRandom } from '../utils/math';

// Origin at top-left, row=Z, col=X. 3-cell rhythm: road, building, building, road, ...
// Roads at every 3rd index (0, 3, 6, ...), last index is a road (requires (GRID_SIZE-1) % 3 === 0).

type GridRow = TileData[];

const CENTER = (GRID_SIZE - 1) / 2;

// Concentric building zones scale with grid so each ring keeps its proportional width.
const SKYSCRAPER_EXTENT = Math.floor(GRID_SIZE / 7);
const COMMERCIAL_EXTENT = Math.floor(GRID_SIZE / 4);
const INDUSTRIAL_EXTENT = Math.floor(GRID_SIZE * 0.4);

export const ROAD_INDICES: Set<number> = (() => {
  const s = new Set<number>();
  for (let i = 0; i < GRID_SIZE; i += 3) s.add(i);
  return s;
})();

function isRoadIndex(i: number): boolean {
  return ROAD_INDICES.has(i);
}

function getBuildingType(row: number, col: number): TileType {
  const dist = Math.max(Math.abs(row - CENTER), Math.abs(col - CENTER));
  if (dist <= SKYSCRAPER_EXTENT) return 'building_skyscraper';
  if (dist <= COMMERCIAL_EXTENT) return 'building_commercial';
  if (dist <= INDUSTRIAL_EXTENT) return 'building_industrial';
  return 'building_suburban';
}

function generateGrid(): GridRow[] {
  const grid: GridRow[] = [];

  for (let row = 0; row < GRID_SIZE; row++) {
    const gridRow: TileData[] = [];
    for (let col = 0; col < GRID_SIZE; col++) {
      const rowIsRoad = isRoadIndex(row);
      const colIsRoad = isRoadIndex(col);

      if (rowIsRoad && colIsRoad) {
        // Both road row AND col → crossroad
        gridRow.push({ type: 'road_crossroad', rotation: 0 });
      } else if (rowIsRoad) {
        // Road row only → horizontal straight (model default is east-west)
        gridRow.push({ type: 'road_straight', rotation: 0 });
      } else if (colIsRoad) {
        // Road col only → vertical straight (rotate 90° to north-south)
        gridRow.push({ type: 'road_straight', rotation: 90 });
      } else {
        // Building tile — type based on distance from center
        const buildingType = getBuildingType(row, col);
        gridRow.push({ type: buildingType, rotation: 0 });
      }
    }
    grid.push(gridRow);
  }

  return grid;
}

export const CITY_GRID: GridRow[] = generateGrid();

// ─── Model variant names ───
const commercialNames = [
  'building-a', 'building-b', 'building-c', 'building-d',
  'building-e', 'building-f', 'building-g', 'building-h',
  'building-i', 'building-j', 'building-k', 'building-l',
  'building-m', 'building-n',
];
const skyscraperNames = [
  'building-skyscraper-a', 'building-skyscraper-b',
  'building-skyscraper-c', 'building-skyscraper-d',
  'building-skyscraper-e',
];
const industrialNames = [
  'building-a', 'building-b', 'building-c', 'building-d',
  'building-e', 'building-f', 'building-g', 'building-h',
  'building-i', 'building-j', 'building-k', 'building-l',
  'building-m', 'building-n', 'building-o', 'building-p',
  'building-q', 'building-r', 'building-s', 'building-t',
];
const suburbanNames = [
  'building-type-a', 'building-type-b', 'building-type-c',
  'building-type-d', 'building-type-e', 'building-type-f',
  'building-type-g', 'building-type-h', 'building-type-i',
  'building-type-j', 'building-type-k', 'building-type-l',
  'building-type-m', 'building-type-n', 'building-type-o',
  'building-type-p', 'building-type-q', 'building-type-r',
  'building-type-s', 'building-type-t', 'building-type-u',
];

// Assign specific model variants to building tiles deterministically
const rng = createSeededRandom(123);

function pick<T>(arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

for (let row = 0; row < GRID_SIZE; row++) {
  for (let col = 0; col < GRID_SIZE; col++) {
    const tile = CITY_GRID[row][col];
    const rotation = Math.floor(rng() * 4) * 90;

    switch (tile.type) {
      case 'building_commercial':
        tile.model = tile.model || pick(commercialNames);
        tile.rotation = rotation;
        break;
      case 'building_skyscraper':
        tile.model = tile.model || pick(skyscraperNames);
        tile.rotation = rotation;
        break;
      case 'building_industrial':
        tile.model = tile.model || pick(industrialNames);
        tile.rotation = rotation;
        break;
      case 'building_suburban':
        tile.model = tile.model || pick(suburbanNames);
        tile.rotation = rotation;
        break;
    }
  }
}

export function tileToWorld(row: number, col: number, tileSize: number): [number, number] {
  const x = (col - (GRID_SIZE - 1) / 2) * tileSize;
  const z = (row - (GRID_SIZE - 1) / 2) * tileSize;
  return [x, z];
}

// Collect the unique set of building model keys actually used by the grid
// so we can skip loading unused variants
function collectUsedModels(): Set<string> {
  const used = new Set<string>();
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      const tile = CITY_GRID[row][col];
      if (tile.model) {
        const category =
          tile.type === 'building_commercial' || tile.type === 'building_skyscraper'
            ? 'commercial'
            : tile.type === 'building_industrial'
              ? 'industrial'
              : 'suburban';
        used.add(`${category}/${tile.model}`);
      }
    }
  }
  return used;
}

export const USED_BUILDING_KEYS = collectUsedModels();
